import { JSXElement, mergeProps } from 'solid-js';
import { ModalSizes } from '~/components/ui/Modal/Modal.tsx';
import { getKbLinkTransformer, Localized } from '~/i18n';
import { externalLinks } from '~/consts.ts';
import { ReadOnlyField } from '~/components/ui/ReadOnlyField';
import { InfoIcon } from 'lucide-solid';
import { Button } from '~/components/ui/Button';
import Modal from '~/components/ui/Modal';
import { getLocalizedString } from '~/i18n/utils.ts';
import { useToast } from '~/components/ui/Toast';
import { useIntl } from '@cookbook/solid-intl';
import { copyTextIntoClipboard } from '~/utils/commonUtils';
import { getApiCredentialsTranslationKey } from '~/pages/Settings/utils.ts';

type GenerateSecretModalProps = {
  secretText: string;
  onClose: () => void;
};

export default function GenerateSecretModal(
  props: GenerateSecretModalProps
): JSXElement {
  const merged = mergeProps({}, props);

  const toaster = useToast();
  const intl = useIntl();

  const closeModal = () => {
    merged.onClose();
  };

  const copySecret = () => {
    copyTextIntoClipboard(merged.secretText).then(() => {
      toaster.toast().success({
        description: getLocalizedString(
          getApiCredentialsTranslationKey('success_copied_client_secret'),
          {
            intl
          }
        )
      });
    });
  };

  return (
    <Modal
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          closeModal();
        }
      }}
      open={!!merged.secretText.length}
      size={ModalSizes.LARGE}
      title={
        <Localized
          translationKey={getApiCredentialsTranslationKey(
            'dialog_title_new_secret'
          )}
        />
      }
      subTitle={
        <Localized
          translationKey={getApiCredentialsTranslationKey(
            'dialog_subtitle_new_secret'
          )}
          vars={{
            kbLink: getKbLinkTransformer(externalLinks.kbArticles.clientSecret)
          }}
        />
      }
      showCloseIcon={true}
    >
      <div
        class={`mt-4 w-full flex items-start rounded-sm text-sm mb-3 px-3 font-medium py-2 bg-[#E6F4FE] border border-[#C2E5FF]`}
      >
        <div class={`mr-2`}>
          <InfoIcon
            width={14}
            class={`text-[#0588F0]`}
          />
        </div>
        <div class={`mt-1 text-sm`}>
          <Localized
            translationKey={getApiCredentialsTranslationKey(
              'warning_copy_client_secret'
            )}
          />
        </div>
      </div>
      <div class={`w-full`}>
        <ReadOnlyField
          label={
            <Localized
              translationKey={getApiCredentialsTranslationKey(
                'field_label_secret'
              )}
            />
          }
          value={merged.secretText}
          width={'full'}
          dataTestId={`settings-client-secret-copy`}
        />
      </div>
      <div class={`flex justify-end mt-4`}>
        <Button
          size={`sm`}
          onClick={copySecret}
          class={`min-w-[100px]`}
          style={{
            'min-width': `${getLocalizedString('cta_labels.close_after_copy').length + 5}ch`
          }}
          data-testid={`settings-client-secret-copy`}
        >
          <Localized translationKey={`cta_labels.close_after_copy`} />
        </Button>
      </div>
    </Modal>
  );
}
